import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ProductCombineCacheService } from 'src/app/services/cache-service/product-combine-cache.service';
import { ProductParentCacheService } from 'src/app/services/cache-service/product-parent-cache.service';
import { ProductCacheService } from 'src/app/services/cache-service/product-cache.service';
import { StockChangeApi } from '../../../../services/api-service/stocks/stock-change-api';
import { BatchAllocationRow, BatchSelectResultDto, DetailBatchAllocationDto } from '../batch-select/models/batch-select';
import { AvailableBatchDto } from '../../../../stocks/product-batch/models/product-batch';

/** 出庫分配建議列 */
export interface SuggestedBatchDto {
  ProductBatchId: number;
  BatchNo: string;
  ExpirationDate: string | null;
  /** 建議分配數量 (正數) */
  Qty: number;
  /** true = 未指定溢流，ProductBatchId=0，BatchNo='未指定' */
  IsUnassigned: boolean;
  /** 該批此列可用量上限 (跨列扣減後的現有量；未提供時退回用 Qty) */
  AvailableQty?: number;
  /** (選填) 該分配列對應的實際(子)商品 ProductId；組合/母商品重開既有分配時，各列指向不同子商品需帶此欄。未提供時 fallback 用 detail 換算後的單一 productId。 */
  ProductId?: number;
}

/** 明細上下文 (父元件傳入) */
export interface BatchAllocateDetailContext {
  /** 明細唯一識別碼 (_uid) */
  uid: string;
  /** 商品 ID */
  productId: number;
  /** 商品名稱 */
  productName: string;
  /** 商品類型 (1=一般, 2=組合) */
  productType: number;
  /** 庫存類別 (1=一般, 2=母商品) */
  stockType: number;
  /** 明細換算後應分配的總數量 */
  qty: number;
}

@Component({
  selector: 'app-stocks-batch-allocate-dialog',
  templateUrl: './batch-allocate-dialog.component.html',
  styleUrls: ['./batch-allocate-dialog.component.css']
})
export class BatchAllocateDialogComponent {

  @Input() show = false;
  @Input() storeRoomId: number = null;
  /** 明細上下文 — 父元件開啟 dialog 前設好 */
  @Input() detail: BatchAllocateDetailContext = null;
  /**
   * 出庫分配建議 (由父元件在開啟 dialog 前設好)。
   * 每列對應一個常規批，含 AvailableQty (現有量上限)。
   */
  @Input() outboundSuggestions: SuggestedBatchDto[] = [];
  /**
   * 各批「跨列可用量上限」(batchId → ceiling = 該批現有量 − 其他明細列已占用)。
   * 父元件提供時，選批以此為上限而非 batch-select 回傳的原始現有量；未提供 (null) 維持原行為。
   */
  @Input() batchCeiling: { [batchId: number]: number } = null;
  /** 入庫模式 (銷退等)：關閉每批「現有量上限」檢查。預設 false 維持出庫行為。 */
  @Input() inboundMode = false;
  /**
   * 子商品 productId → 可用批清單 (父元件預先取得、依扣庫規則撈好的批)。父元件提供時，每列 batch-select
   * 下拉改吃此清單、不自行呼叫 GetAvailableBatches，使下拉集合與 ceiling 一致。
   * null (預設) 維持原行為 (batch-select 自行呼叫 GetAvailableBatches)。
   */
  @Input() availableBatchesByProduct: { [productId: number]: AvailableBatchDto[] } = null;

  /** 過期比較基準日 (單據日期)；轉傳給每列 batch-select 的 referenceDate。未提供則今天。 */
  @Input() referenceDate: Date | string = null;

  /** 子商品 productId → 未指定批(溢流赤字池)現有量；提供時下拉「未指定」選項顯示池餘量 (可能為負)。
   *  null (預設) = dialog 於分配列建立後自行載入 (銷退等未預載可用批的頁面)。 */
  @Input() unassignedQtyByProduct: { [productId: number]: number } = null;

  @Output() confirm = new EventEmitter<DetailBatchAllocationDto>();
  @Output() close = new EventEmitter<void>();

  /** 分配列 (初值安全空陣列，避免 (open) 觸發前模板存取 undefined) */
  rows: BatchAllocationRow[] = [];
  /** 明細換算後應分配總量 */
  requiredQty = 0;
  /** 已分配量 (快取屬性，不在模板做 reduce) */
  allocatedQty = 0;
  /** 未指定溢流數量 = requiredQty − sum(常規批)；出庫模式專用 (快取屬性) */
  unassignedQty = 0;
  /** 是否可以確認 (快取 boolean，避免模板每次 CD 都做 every) */
  canConfirm = false;
  /** 確認被擋住的原因 (canConfirm=false 時顯示給使用者；空字串=無錯誤) */
  errorMessage = '';
  /** 是否正在展開子商品 (async) */
  isLoading = false;

  /** 模板實際使用的池餘量 map：優先用父元件輸入；未提供時 dialog 自行載入 */
  unassignedQtyMap: { [productId: number]: number } = null;

  /** 母商品的母子換算說明 (母商品時顯示；例: 對應子商品:「000001 xxx」（1 盒 = 100 錠）) */
  parentConversionHint = '';

  constructor(
    private productCombineCache: ProductCombineCacheService,
    private productParentCache: ProductParentCacheService,
    private productCacheService: ProductCacheService,
    private stockChangeApi: StockChangeApi
  ) {}

  /**
   * 是否為組合商品 (ProductType=2)。組合的分配列由子商品固定展開、數量由組合比例決定，
   * 使用者只能為各子商品選批號：不可新增/刪除分配列、不可改數量。
   */
  get isCombination(): boolean {
    return !!this.detail && this.detail.productType === 2;
  }

  // Dialog 生命週期
  /** Dialog 開啟事件：依明細類型展開子商品並建立初始分配列 */
  async onDialogOpen(): Promise<void> {
    this.rows = [];
    this.requiredQty = this.detail ? (this.detail.qty || 0) : 0;
    this.unassignedQty = 0;
    this.parentConversionHint = '';
    this.unassignedQtyMap = this.unassignedQtyByProduct;
    this.recalc();

    if (!this.detail) { return; }

    // 母商品：顯示母子換算說明
    if (this.detail.stockType === 2) {
      const convInfo = await this.productParentCache.getConversionInfo(this.detail.productId);
      if (convInfo) {
        this.parentConversionHint =
          `對應子商品:「${convInfo.ChildProductCode} ${convInfo.ChildProductName}」` +
          `（1 ${convInfo.ParentUnit || '單位'} = ${convInfo.ConversionQty} ${convInfo.ChildUnit || '單位'}）`;
      }
    }

    // 若父元件已提供分配建議，直接預帶常規批列
    if (this.outboundSuggestions && this.outboundSuggestions.length > 0) {
      // 母商品的批號實際掛在子商品上，列的 ProductId/Name 需用子商品，
      // 否則 batch-select 以母商品 ID 查不到批號（手動新增/改批號時）。
      let rowProductId = this.detail.productId;
      let rowProductName = this.detail.productName;
      if (this.detail.stockType === 2) {
        const conv = await this.productParentCache.getConversionInfo(this.detail.productId);
        if (conv) {
          rowProductId = conv.ChildProductId;
          rowProductName = conv.ChildProductName;
          // 母商品：應分配量也要換算成子商品單位，否則「應分配(母單位)」會與「已分配(子單位)」對不上
          this.requiredQty = (this.detail.qty || 0) * conv.ConversionQty;
        }
      } else if (this.detail.productType === 2) {
        // 組合商品重開：requiredQty 重算為各子商品需求合計
        // 否則沿用組合本身 qty 會讓 recalc 誤判「已分配 > 應分配」導致確定鈕卡死。
        const subItems = await this.productCombineCache.getCombineDetailsWithQty(this.detail.productId, this.detail.qty);
        if (subItems && subItems.length > 0) {
          // 只計「批號子商品」的需求量（與 _buildRowsForCombine 的建列過濾一致），
          // 否則非批號子商品會灌進 requiredQty，rows 卻沒有對應列 → 差額變成幽靈「未指定」。
          let total = 0;
          for (const sub of subItems) {
            if (sub.StockType === 2) {
              // 母子商品：批號掛孫商品，查孫商品批號旗標，孫商品用批號才計入
              const subConv = await this.productParentCache.getConversionInfo(sub.ProductId);
              if (!subConv) { continue; }
              const grandChild = this.productCacheService.getProductById(subConv.ChildProductId);
              if (!grandChild || !grandChild.IsUseBatchNo) { continue; }
              total += sub.RequiredQty * subConv.ConversionQty;
            } else {
              if (!sub.IsUseBatchNo) { continue; }
              total += sub.RequiredQty;
            }
          }
          this.requiredQty = total;
        }
      }
      const rowProduct = this.productCacheService.getProductById(rowProductId) || null;  // 供商品名稱欄 tags 顯示
      this.rows = this.outboundSuggestions
        .filter(s => !s.IsUnassigned)
        .map(s => {
          // 組合/母商品重開：各列可能指向不同子商品，優先用建議列自帶的 ProductId；
          // 未提供 (退貨/銷售既有呼叫方尚未帶) 則 fallback 回單一 rowProductId，維持原行為
          const pid = (s.ProductId != null && s.ProductId > 0) ? s.ProductId : rowProductId;
          const prod = pid === rowProductId ? rowProduct : (this.productCacheService.getProductById(pid) || null);
          return {
            ProductId: pid,
            ProductName: prod ? prod.ProductName : rowProductName,
            ProductBatchId: s.ProductBatchId,
            BatchNo: s.BatchNo,
            ExpirationDate: s.ExpirationDate,
            Qty: s.Qty,
            IsNew: false,
            IsUnassigned: false,
            AvailableQty: s.AvailableQty != null ? s.AvailableQty : s.Qty,  // 跨列扣減後的可用量上限；未提供退回用 Qty
            _product: prod
          };
        });
      // 分配中含未指定量 → 還原「已勾選未指定」的列（否則重開 dialog 該列會消失）
      const unassignedSuggestions = this.outboundSuggestions.filter(s => s.IsUnassigned);
      if (unassignedSuggestions.length > 0) {
        if (this.isCombination) {
          // 組合：每個未指定子商品各自還原一列，帶回自己的 ProductId 與 Qty（不合併、不歸零），
          // 否則多個未指定子商品會掉列、數量歸零。
          const unassignedRows = unassignedSuggestions.map(us => {
            const upid = (us.ProductId != null && us.ProductId > 0) ? us.ProductId : rowProductId;
            const uprod = upid === rowProductId ? rowProduct : (this.productCacheService.getProductById(upid) || null);
            return {
              ProductId: upid,
              ProductName: uprod ? uprod.ProductName : rowProductName,
              ProductBatchId: 0,
              BatchNo: '',
              ExpirationDate: null,
              Qty: us.Qty,
              IsNew: false,
              IsUnassigned: true,
              _product: uprod
            };
          });
          this.rows = [...this.rows, ...unassignedRows];
        } else {
          // 單一商品：還原一列未指定，數量由 recalc 同步為差額（不直接沿用建議值）
          const us = unassignedSuggestions[0];
          const upid = (us.ProductId != null && us.ProductId > 0) ? us.ProductId : rowProductId;
          const uprod = upid === rowProductId ? rowProduct : (this.productCacheService.getProductById(upid) || null);
          this.rows = [...this.rows, {
            ProductId: upid,
            ProductName: uprod ? uprod.ProductName : rowProductName,
            ProductBatchId: 0,
            BatchNo: '',
            ExpirationDate: null,
            Qty: 0,
            IsNew: false,
            IsUnassigned: true,
            _product: uprod
          }];
        }
      }
      this.recalc();
      this._loadUnassignedQtyIfNeeded();
      return;
    }

    // 無建議時：依商品類型展開子商品
    this.isLoading = true;
    try {
      if (this.detail.productType === 2) {
        // 組合商品：展開子商品，各子商品一列，requiredQty 由子商品 RequiredQty 決定
        await this._buildRowsForCombine();
      } else if (this.detail.stockType === 2) {
        // 母商品：換算成子商品一列
        await this._buildRowsForParent();
      } else {
        // 一般商品：單列，ProductId = 自己
        this.rows = [this._makeEmptyRow(this.detail.productId, this.detail.productName, this.requiredQty)];
      }
    } catch (e) {
      console.error('[BatchAllocateDialog] 展開子商品失敗', e);
      // fallback: 以原商品建一列
      this.rows = [this._makeEmptyRow(this.detail.productId, this.detail.productName, this.requiredQty)];
    } finally {
      this.isLoading = false;
    }
    this.recalc();
    this._loadUnassignedQtyIfNeeded();
  }

  /** 父元件未提供池餘量時，依分配列的子商品自行載入（顯示用，失敗僅略過不擋分配操作） */
  private _loadUnassignedQtyIfNeeded(): void {
    if (this.unassignedQtyByProduct != null || !this.storeRoomId) { return; }
    const pids = Array.from(new Set(this.rows.map(r => r.ProductId).filter(pid => pid > 0)));
    if (pids.length === 0) { return; }
    this.stockChangeApi.getAvailableBatchesForAllocation(
      { storeRoomId: this.storeRoomId, productIds: pids, changeDate: this.referenceDate || undefined }, false)
      .then(res => {
        const map: { [productId: number]: number } = {};
        (res || []).forEach(r => { map[r.ProductId] = r.UnassignedQty || 0; });
        this.unassignedQtyMap = map;
      })
      .catch(() => { /* 顯示用資料，載入失敗不影響分配 */ });
  }

  closeDialog(): void {
    this.close.emit();
  }

  // 列操作
  /** 新增一列 (同子商品分多批時使用) */
  addRow(productId: number, productName: string): void {
    this.rows = [...this.rows, this._makeEmptyRow(productId, productName, 0)];
    this.recalc();
  }

  /** 「新增分配列」按鈕：以最後一列的商品為基礎複製一列；
   *  rows 為空時 (展開 fallback) 以明細自身商品建立一列，避免存取 rows[-1] 崩潰 */
  addLastProductRow(): void {
    if (this.rows.length === 0) {
      this.addRow(
        this.detail ? this.detail.productId : 0,
        this.detail ? this.detail.productName : ''
      );
      return;
    }
    const last = this.rows[this.rows.length - 1];
    this.addRow(last.ProductId, last.ProductName);
  }

  removeRow(index: number): void {
    // 以新陣列參考重指派
    this.rows = this.rows.filter((_, i) => i !== index);
    this.recalc();
  }

  /** app-stocks-batch-select 選擇事件；選「未指定」→ 此列改走未指定池，數量由 recalc 同步為差額 */
  onRowBatchSelected(row: BatchAllocationRow, ev: BatchSelectResultDto): void {
    if (ev.IsUnassigned) {
      row.IsUnassigned = true;
      row.ProductBatchId = 0;
      row.BatchNo = '';
      row.ExpirationDate = null;
      row.IsNew = false;
      row.AvailableQty = undefined;
      row._overStock = false;
      this.recalc();
      return;
    }
    row.IsUnassigned = false;
    row.ProductBatchId = ev.ProductBatchId;
    row.BatchNo = ev.BatchNo;
    row.ExpirationDate = ev.ExpirationDate;
    row.IsNew = ev.IsNew;
    // 以該批「跨列可用量上限」為此列扣庫上限 (父元件提供時優先；否則退回原始現有量)，手動也不可超扣。
    row.AvailableQty = (this.batchCeiling && this.batchCeiling[ev.ProductBatchId] != null)
      ? this.batchCeiling[ev.ProductBatchId]
      : ev.CurrentQty;
    this.recalc();
  }

  /** 數量變更 */
  onQtyChange(): void {
    this.recalc();
  }

  // 確認
  confirmAllocate(): void {
    if (!this.canConfirm) { return; }
    // 空白列（未選批號且未填數量）略過；勾「未指定」的列為標記列，
    // 其數量已含在 unassignedQty，由下方合併為單一未指定列送出 (避免重複計算)
    const allocations: BatchAllocationRow[] = this.rows
      .filter(r => !r.IsUnassigned && !this._isBlankRow(r))
      .map(r => ({ ...r }));
    if (this.isCombination) {
      // 組合：每個未指定子商品各自送一筆（保留各子商品 ProductId 與其固定 Qty）。
      // 不可合併成單筆，否則多子商品的未指定量會全掛到同一個 ProductId。
      this.rows
        .filter(r => r.IsUnassigned && (Number(r.Qty) || 0) > 0)
        .forEach(r => {
          allocations.push({
            ProductId: r.ProductId,
            ProductName: r.ProductName,
            ProductBatchId: 0,
            BatchNo: '未指定',
            ExpirationDate: null,
            Qty: Number(r.Qty) || 0,
            IsNew: false,
            IsUnassigned: true
          });
        });
    } else if (this.unassignedQty > 0) {
      // 單一商品：未指定量合併為一筆（母商品已換算為子商品，ProductId 取任一列即可）。
      const anyRow = this.rows.find(r => r.ProductId > 0);
      allocations.push({
        ProductId: anyRow ? anyRow.ProductId : this.detail.productId,
        ProductName: anyRow ? anyRow.ProductName : this.detail.productName,
        ProductBatchId: 0,
        BatchNo: '未指定',
        ExpirationDate: null,
        Qty: this.unassignedQty,
        IsNew: false,
        IsUnassigned: true
      });
    }
    this.confirm.emit({
      DetailUid: this.detail.uid,
      Allocations: allocations
    });
  }

  // 私有 helpers
  /** 空白列：未選批號且未填數量 (數量為 0/空) → 確認時直接略過，不驗證、不送出 */
  private _isBlankRow(r: BatchAllocationRow): boolean {
    return !r.BatchNo && (Number(r.Qty) || 0) === 0;
  }

  /** 快取：計算 allocatedQty / unassignedQty / canConfirm / errorMessage，不在模板做陣列走訪 */
  private recalc(): void {
    const regularRows = this.rows.filter(r => !r.IsUnassigned);
    // 空白列 (未選批號且未填數量)略過，不納入驗證與合計
    const activeRows = regularRows.filter(r => !this._isBlankRow(r));
    const regularSum = activeRows.reduce((s, r) => s + (Number(r.Qty) || 0), 0);
    this.allocatedQty = regularSum;
    if (this.isCombination) {
      // 組合：每個子商品一列、Qty 固定。未指定量 = 各未指定子商品列自身 Qty 合計
      // （每個子商品各自成一筆未指定，不合併、不覆寫 Qty）。
      this.unassignedQty = this.rows
        .filter(r => r.IsUnassigned)
        .reduce((s, r) => s + (Number(r.Qty) || 0), 0);
    } else {
      // 單一商品：未指定量 = 應分配 − 常規批已分配；未指定列 Qty 同步為此差額（唯讀顯示），
      // 讓使用者直接看到將歸入未指定池的量
      this.unassignedQty = Math.max(this.requiredQty - regularSum, 0);
      this.rows.filter(r => r.IsUnassigned).forEach(r => { r.Qty = this.unassignedQty; });
    }

    // 同一批號可能被拆成多列 → 先彙總「該批合計量」，以合計比對上限，
    // 避免各列各自 ≤ 上限但合計超量卻不擋（單一批實際可扣量固定，跨列合計不可超過）。
    const qtyByBatch: { [batchId: number]: number } = {};
    for (const r of activeRows) {
      if (r.ProductBatchId > 0) {
        qtyByBatch[r.ProductBatchId] = (qtyByBatch[r.ProductBatchId] || 0) + (Number(r.Qty) || 0);
      }
    }

    // 逐列檢查 (僅非空白列)：超過該批可用庫存的列 (紅框)、未選批號、數量非正
    let hasOverStock = false;
    let hasMissingBatch = false;
    let hasInvalidQty = false;
    let overStockBatchNo = '';
    for (const r of regularRows) { r._overStock = false; }  // 先清掉空白列殘留的紅框標記
    for (const r of activeRows) {
      const qty = Number(r.Qty) || 0;
      const ceiling = (r.AvailableQty !== undefined && r.AvailableQty !== null) ? r.AvailableQty : null;
      // 同批多列以合計判斷 (合計超量時整批相關列都標紅)；未選批號的列退回用自身量
      const batchTotal = r.ProductBatchId > 0 ? (qtyByBatch[r.ProductBatchId] || qty) : qty;
      r._overStock = !this.inboundMode && ceiling !== null && batchTotal > ceiling;
      if (r._overStock) {
        hasOverStock = true;
        if (!overStockBatchNo) { overStockBatchNo = r.BatchNo || ''; }
      }
      if (!r.BatchNo) { hasMissingBatch = true; }   // 有填數量但未選批號
      if (qty <= 0) { hasInvalidQty = true; }        // 零和負數都不允許
    }

    this.canConfirm =
      !this.isLoading &&
      (activeRows.length > 0 || this.unassignedQty > 0) &&
      !hasMissingBatch &&
      !hasInvalidQty &&
      !hasOverStock &&
      regularSum <= this.requiredQty;

    // 確認被擋住的原因（依優先序顯示一則，讓使用者知道為何不能確認）
    if (regularSum > this.requiredQty) {
      this.errorMessage = `已分配 ${regularSum}，超過應分配 ${this.requiredQty}，請減少分配數量。`;
    } else if (hasOverStock) {
      this.errorMessage = overStockBatchNo
        ? `批號「${overStockBatchNo}」的分配量超過該批可用庫存，請調整數量。`
        : '分配量超過批號可用庫存，請調整數量。';
    } else if (hasInvalidQty) {
      this.errorMessage = '分配數量必須大於 0。';
    } else if (hasMissingBatch) {
      this.errorMessage = '尚有分配列未選擇批號。';
    } else {
      this.errorMessage = '';
    }
  }

  /** 建立空白分配列 */
  private _makeEmptyRow(productId: number, productName: string, qty: number): BatchAllocationRow {
    return {
      ProductId: productId,
      ProductName: productName,
      ProductBatchId: 0,
      BatchNo: '',
      ExpirationDate: null,
      Qty: qty,
      IsNew: false,
      _product: this.productCacheService.getProductById(productId) || null  // 供商品名稱欄 tags 顯示
    };
  }

  /** 組合商品展開: 查 ProductCombineCacheService，每個子商品一列
   *  子商品若為母商品(StockType=2)，再透過 ProductParentCacheService 換算 */
  private async _buildRowsForCombine(): Promise<void> {
    const parentQty = this.detail.qty;
    const subItems = await this.productCombineCache.getCombineDetailsWithQty(this.detail.productId, parentQty);
    if (!subItems || subItems.length === 0) {
      // fallback: 以組合商品本身建一列
      this.rows = [this._makeEmptyRow(this.detail.productId, this.detail.productName, this.requiredQty)];
      return;
    }

    const resultRows: BatchAllocationRow[] = [];
    for (const sub of subItems) {
      if (sub.StockType === 2) {
        // 子商品是母商品：批號掛在孫商品上。母自身 IsUseBatchNo 不可靠 (恆 false)，
        // 故查孫商品的批號旗標，孫商品為批號商品才建列。
        const conv = await this.productParentCache.getConversionInfo(sub.ProductId);
        if (!conv) {
          // 查無母子換算 → 無法確定孫商品批號狀態，跳過 (不建列)
          continue;
        }
        const grandChild = this.productCacheService.getProductById(conv.ChildProductId);
        if (!grandChild || !grandChild.IsUseBatchNo) {
          // 孫商品非批號商品 → 不需分配批號
          continue;
        }
        const childQty = sub.RequiredQty * conv.ConversionQty;
        resultRows.push(this._makeEmptyRow(conv.ChildProductId, conv.ChildProductName, childQty));
      } else {
        // 一般子商品：非批號商品不需分配批號
        if (!sub.IsUseBatchNo) {
          continue;
        }
        resultRows.push(this._makeEmptyRow(sub.ProductId, sub.ProductName, sub.RequiredQty));
      }
    }
    this.rows = resultRows;

    // requiredQty 重新計算為各子商品 RequiredQty 合計 (含母→子換算後)
    this.requiredQty = this.rows.reduce((s, r) => s + r.Qty, 0);
  }

  /** 母商品展開: 查 ProductParentCacheService，得到子商品 × 換算量 */
  private async _buildRowsForParent(): Promise<void> {
    const conv = await this.productParentCache.getConversionInfo(this.detail.productId);
    if (!conv) {
      // 無換算設定，fallback 以母商品本身建一列
      this.rows = [this._makeEmptyRow(this.detail.productId, this.detail.productName, this.requiredQty)];
      return;
    }
    const childQty = this.detail.qty * conv.ConversionQty;
    this.rows = [this._makeEmptyRow(conv.ChildProductId, conv.ChildProductName, childQty)];
    // requiredQty 更新為子商品的換算數量
    this.requiredQty = childQty;
  }
}
